import { CommonModule } from '@angular/common';
import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, inject, Input, OnChanges,
  OnDestroy, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { Spine } from '@esotericsoftware/spine-pixi-v8/dist/Spine';
import { TeamIdAndColorMap } from '@nova/configs';
import {
  DEFAULT_BOT_TEAM_COLOR,
  SpineObjectFactory
} from '@nova/game-common-framework/spine';
import { CompletePartDataSet } from '@nova/types';
import { Application, Container } from 'pixi.js';

import { Logger } from 'app/core/logger/services/logger.service';
import { KhAnalyticsService } from 'app/shared/services/kh-analytics.service';
import { SharedModule } from 'app/shared/shared.module';
import { checkWebGLSupport } from 'app/shared/utils/webgl-check';

@Component({
  selector: 'kh-async-avatar-loader',
  templateUrl: './async-avatar-loader.component.html',
  styleUrls: ['./async-avatar-loader.component.less'],
  imports: [CommonModule, SharedModule]
})
export class AsyncAvatarLoadComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('spineContainer')
  public spineContainer?: ElementRef<HTMLDivElement>;

  @Input() public width = 300;
  @Input() public height = 300;

  @Input() public facingDirection: 'left' | 'right' = 'right';

  @Input() public parts?: CompletePartDataSet;
  @Output() public avatarLoading = new EventEmitter<boolean>();

  protected _viewInitialized = false;
  protected _pendingParts?: CompletePartDataSet;

  protected _app?: Application;
  protected _botSpine?: Spine;

  protected _botSpineContainer?: Container;

  protected readonly BOT_BOTTOM_PADDING = 190;
  protected readonly BOT_Z_INDEX = 2;

  /** Ally team color — baked into tint-slot atlas pages at load time (canvas-safe). */
  protected readonly TEAM_COLOR = TeamIdAndColorMap[0];

  /** Slots whose team color is baked into atlas pages at load time. */
  protected readonly BAKED_TEAM_COLOR_SLOTS = new Set(['eyes', 'lights']);

  public loading = false;

  protected readonly cdRef = inject(ChangeDetectorRef);
  protected readonly khAnalyticsService = inject(KhAnalyticsService);
  protected readonly logger = inject(Logger);

  public ngAfterViewInit(): void {
    this._viewInitialized = true;
    checkWebGLSupport(this.khAnalyticsService);

    // Load pending parts if they were set before view initialization
    if (this._pendingParts) {
      this.loadFramework(this._pendingParts).catch((error: unknown) => {
        this.logger.error('loadFrame', error);
      });
      this._pendingParts = undefined;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.parts && this.parts) {
      if (this._viewInitialized) {
        this.loadFramework(this.parts).catch((error: unknown) => {
          this.logger.error('loadFrame', error);
        });
      }
      else {
        // Store parts to load after view initialization
        this._pendingParts = this.parts;
      }
    }
  }

  protected async loadFramework(parts: CompletePartDataSet) {
    if (this.loading) {
      return;
    }

    this.setLoadingStart();

    try {
      if (!this.spineContainer) {
        throw new Error('spine container is null');
      }

      // Hide canvas IMMEDIATELY to prevent visual glitch (twitch) when swapping parts
      if (this._app?.canvas) {
        this._app.canvas.style.visibility = 'hidden';
      }

      const container = this.spineContainer.nativeElement;
      await this.setupRenderer(container, parts);

      this.adjustCanvasScale();

      if (this._app?.canvas) {
        this._app.canvas.style.visibility = 'visible';
      }
    }
    finally {
      this.setLoadingComplete();
      this.cdRef.detectChanges();
    }
  }

  protected async createPixiApp(parent: HTMLElement) {
    try {
      const app = new Application();
      await app.init({
        backgroundAlpha: 0,
        height: this.height,
        width: this.width,
        preference: ['webgl', 'canvas']
      });

      (globalThis as any).__PIXI_APP__ = app;
      parent.appendChild(app.canvas); // one time
      app.canvas.setAttribute('data-testid', 'avatar-canvas');

      await SpineObjectFactory.initializeStandAlone(app);
      this._botSpineContainer = new Container();
      app.stage.addChild(this._botSpineContainer);
      this._botSpineContainer.zIndex = this.BOT_Z_INDEX;
      app.stage.sortChildren();

      return app;
    }
    catch (error) {
      this.logger.error('Failed to create Pixi app', error);
      throw error;
    }
  }

  protected async setupRenderer(container: HTMLDivElement, partData: CompletePartDataSet) {
    if (!this._app) {
      this._app = await this.createPixiApp(container);
      // Hide canvas immediately after creation to prevent twitch on first load
      this._app.canvas.style.visibility = 'hidden';
    }

    this.cleanupBot();
    if (this._app) {
      await this.initBotSpine(partData);
    }

    this.updateBotFacingDirection(this.facingDirection);

    const tintSlots = this.extractTintSlotsFromEquippedParts(this.parts);
    this.applyTeamColorToBot(tintSlots ?? []);
  }

  @HostListener('window:resize', ['$event'])
  public onWindowResize(): void {
    this.adjustCanvasScale();
  }

  protected adjustCanvasScale(): void {
    if (!this._app || !this.spineContainer) {
      return;
    }

    const container = this.spineContainer.nativeElement;
    const canvas = this._app.canvas;
    const containerWidth = container.clientWidth;

    if (containerWidth <= 0) {
      return;
    }

    const scale = containerWidth / canvas.width;
    const displayHeight = canvas.height * scale;

    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${displayHeight}px`;
    canvas.style.scale = '';
    canvas.style.transform = '';

    container.style.height = `${displayHeight}px`;
  }

  public ngOnDestroy(): void {
    if (this._app) {
      this._app.canvas.style.visibility = 'hidden';
    }

    this._app?.destroy(true, {
      children: true,
      context: true
    });
    this._app = undefined;
    this.parts = undefined;
    this._viewInitialized = false;
  }

  protected setLoadingStart(): void {
    this.loading = true;
    this.avatarLoading.emit(true);
  }

  protected setLoadingComplete(): void {
    this.loading = false;
    this.avatarLoading.emit(false);
  }

  protected extractTintSlotsFromEquippedParts(equippedParts?: CompletePartDataSet): string[] | undefined {
    if (!equippedParts) {
      return undefined;
    }
    const tintSlots = new Set<string>();
    for (const part of Object.values(equippedParts)) {
      if (!part || !part.tintSlots) { continue; }
      for (const slot of part.tintSlots) {
        tintSlots.add(slot);
      }
    }
    return Array.from(tintSlots);
  }

  protected cleanupBot() {
    if (!this._botSpine) { return; }
    this._botSpineContainer?.removeChild(this._botSpine);
    this._botSpine.destroy({ children: true });
    this._botSpine = undefined;
  }

  protected async initBotSpine(parts: CompletePartDataSet): Promise<Spine> {
    try {
      if (!this._app) {
        throw new Error('no app at initBotSpine');
      }

      this._botSpine = await SpineObjectFactory.loadAndCreateBot(
        parts,
        undefined,
        { teamColor: DEFAULT_BOT_TEAM_COLOR }
      );

      if (this._botSpine && this._app?.canvas) {
        this._botSpine.position.set(this._app?.canvas.width / 2, this._app?.canvas.height - this.BOT_BOTTOM_PADDING);
        this._botSpineContainer?.addChild(this._botSpine);
      }
      return this._botSpine;
    }
    catch (error) {
      this.logger.error('Failed to initialize bot spine', error);
      throw error;
    }
  }

  protected updateBotFacingDirection(facingDirection: 'left' | 'right'): void {
    if (this._botSpine) {
      this._botSpine.skeleton.scaleX = facingDirection === 'left' ? -1 : 1;
    }
  }

  /**
   * Resets team-color slot multipliers to white so atlas tints display correctly.
   */
  protected applyTeamColorToBot(tintSlots: string[]): void {
    if (!tintSlots || tintSlots.length === 0) {
      return;
    }

    const skeleton = this._botSpine?.skeleton;

    for (const slotName of tintSlots) {
      const slot = skeleton?.findSlot(slotName);
      if (!slot) {
        console.error('applyTeamColorToBot: slot not found', slotName);
        continue;
      }

      if (this.BAKED_TEAM_COLOR_SLOTS.has(slotName)) {
        slot.color.set(1, 1, 1, slot.data.color.a);
      }
    }
  }
}
